import { Stack, useLocalSearchParams } from 'expo-router';
import { ChallengeType } from 'features/auth/resetPassword/constants/ChallengeType';
import { useInitiateChallenge } from 'features/auth/resetPassword/hooks/useInitiateChallenge/useInitiateChallenge';
import { useSelectChallenge } from 'features/auth/resetPassword/hooks/useSelectChallenge/useSelectChallenge';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

type ConfirmIdentitySearchParams = {
  is1FA?: string;
  identityId?: string;
  email: string;
};

const ConfirmIdentity = () => {
  const insets = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('auth', {
    keyPrefix: 'resetPassword.confirmIdentity',
  });
  const { is1FA, identityId, email } =
    useLocalSearchParams<ConfirmIdentitySearchParams>();
  const allow1FA: boolean = is1FA === 'true';
  const { challenges, loading: initiateChallengeLoading } =
    useInitiateChallenge(allow1FA, identityId);
  const { selectChallenge, loading: selectChallengeLoading } =
    useSelectChallenge(email);
  const setupPresentedAnalyticsEvent = usePressableAnalytics({
    analyticsId: 'setupPresented',
    customAttributes: {
      challenge_presented: allow1FA ? '1fa' : '2fa',
    },
  });
  const handleChallengeNavigation = (selectChallengeID: string) => {
    void selectChallenge({
      challengeId: selectChallengeID,
      identityId: String(identityId),
      allow1FA,
    });
  };

  useEffect(() => {
    setupPresentedAnalyticsEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let emailChallengeID: string = '';
  let smsChallengeID: string = '';

  challenges?.map((challenge) => {
    if (challenge.challengeType === (ChallengeType.Email as string)) {
      emailChallengeID = challenge.challengeId;
    } else if (
      challenge.challengeType === (ChallengeType.TextMessage as string)
    ) {
      smsChallengeID = challenge.challengeId;
    }
  });

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      <View style={styles.subContainer}>
        <Stack.Screen
          options={{
            title: translatedText('title'),
            headerShown: true,
            headerBackVisible: true,
            header: () => (
              <NavigationBar
                title={translatedText('title')}
                primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
                primaryOnPress={navigateBack}
                transparent={false}
              />
            ),
          }}
        />
        <ScreenTransitionOverlay
          isLoading={initiateChallengeLoading || selectChallengeLoading}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={null}
        />
        <View>
          <View style={styles.dividerContainer}>
            <TitleLgText style={styles.headingContainer} numberOfLines={0}>
              {translatedText('header')}
            </TitleLgText>
            <BodyBaseText style={styles.subHeadingContainer} numberOfLines={0}>
              {translatedText('description')}
            </BodyBaseText>
          </View>

          {emailChallengeID !== '' ? (
            <View style={styles.listContainer}>
              <ListItem
                primaryCopy={translatedText('section.email')}
                onPress={() => {
                  handleChallengeNavigation(emailChallengeID);
                }}
                analyticsId="emailSelected"
                customAttributes={{
                  challenge_method: translatedText('email'),
                }}
                leadingAccessory={{
                  id: '',
                  name: VXLComponentId.ICON,
                  props: {
                    iconKey: 'EMAIL',
                    iconSize: 'MD',
                  },
                }}
              />
            </View>
          ) : null}

          {smsChallengeID !== '' ? (
            <View>
              <View style={styles.dividerContainer}>
                <HairLineDivider />
              </View>

              <View style={styles.listContainer}>
                <ListItem
                  primaryCopy={translatedText('section.message')}
                  onPress={() => {
                    handleChallengeNavigation(smsChallengeID);
                  }}
                  analyticsId="phoneSelected"
                  customAttributes={{
                    challenge_method: translatedText('phone'),
                  }}
                  leadingAccessory={{
                    id: '',
                    name: VXLComponentId.ICON,
                    props: {
                      iconKey: 'TEXT',
                      iconSize: 'MD',
                    },
                  }}
                />
              </View>
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    minHeight: `100%`,
    minWidth: `100%`,
  },
  dividerContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  headingContainer: {
    color: theme.color.text.primary,
    paddingVertical: theme.spacing.m,
  },
  listContainer: {
    height: 58,
  },
  subContainer: {
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    width: '100%',
  },

  subHeadingContainer: {
    color: theme.color.text.primary,
    marginBottom: theme.spacing.m,
  },
});

export default ConfirmIdentity;
